/**
 * Navigation Module
 *
 * Handles sticky header, mobile menu toggle, keyboard/click-outside
 * dismissal, smooth-scroll anchor links and active-link highlighting.
 *
 * @module Navigation
 */
export default class Navigation {

  static DEFAULTS = {
    stickyThreshold: 50,
    mobileBreakpoint: 1024,
    scrollOffset: 80,
    selectors: {
      header: '[data-header]',
      nav: '[data-nav]',
      toggle: '[data-nav-toggle]',
      links: '[data-nav] a[href^="#"]',
      sections: 'section[id]',
    },
  };

  /**
   * @param {Object} options - Merged with DEFAULTS.
   */
  constructor(options = {}) {
    this.config = { ...Navigation.DEFAULTS, ...options };

    this.header = document.querySelector(this.config.selectors.header);
    this.nav = document.querySelector(this.config.selectors.nav);
    this.toggle = document.querySelector(this.config.selectors.toggle);
    this.links = document.querySelectorAll(this.config.selectors.links);
    this.sections = document.querySelectorAll(this.config.selectors.sections);

    if (!this.header || !this.nav) {
      return;
    }

    this._lastScrollY = 0;
    this._ticking = false;

    this._init();
  }

  /* ------------------------------------------------------------------
   * Bootstrap
   * ----------------------------------------------------------------*/

  _init() {
    this._initState();
    this._bindEvents();
    this._onScroll();           // set initial state
    this._highlightActive();    // set initial active link
  }

  /**
   * Apply initial data-state attributes so CSS transitions work from
   * a known starting point.
   */
  _initState() {
    this.header.setAttribute('data-state', '');
    this.nav.setAttribute('data-state', 'closed');

    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-controls', this.nav.id || '');
    }
  }

  /* ------------------------------------------------------------------
   * Event binding
   * ----------------------------------------------------------------*/

  _bindEvents() {
    // Scroll — throttled via rAF
    window.addEventListener('scroll', this._requestTick.bind(this), { passive: true });

    // Mobile toggle
    if (this.toggle) {
      this.toggle.addEventListener('click', this._handleToggle.bind(this));
    }

    // ESC key closes mobile menu
    document.addEventListener('keydown', this._handleKeydown.bind(this));

    // Click outside closes mobile menu
    document.addEventListener('click', this._handleClickOutside.bind(this));

    // Smooth-scroll anchor links
    this.links.forEach((link) => {
      link.addEventListener('click', this._handleAnchorClick.bind(this));
    });

    // Re-evaluate active link on resize (breakpoint may change)
    window.addEventListener('resize', this._handleResize.bind(this), { passive: true });
  }

  /* ------------------------------------------------------------------
   * Sticky header
   * ----------------------------------------------------------------*/

  _requestTick() {
    this._lastScrollY = window.scrollY;

    if (!this._ticking) {
      window.requestAnimationFrame(() => {
        this._onScroll();
        this._highlightActive();
        this._ticking = false;
      });
      this._ticking = true;
    }
  }

  _onScroll() {
    const isSticky = this._lastScrollY > this.config.stickyThreshold;
    const current = this.header.getAttribute('data-state');

    if (isSticky && current !== 'sticky') {
      this.header.setAttribute('data-state', 'sticky');
    } else if (!isSticky && current === 'sticky') {
      this.header.setAttribute('data-state', '');
    }
  }

  /* ------------------------------------------------------------------
   * Mobile menu
   * ----------------------------------------------------------------*/

  _isMenuOpen() {
    return this.nav.getAttribute('data-state') === 'open';
  }

  _openMenu() {
    this.nav.setAttribute('data-state', 'open');
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'true');
    }
    document.body.style.overflow = 'hidden';
  }

  _closeMenu() {
    this.nav.setAttribute('data-state', 'closed');
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
    }
    document.body.style.overflow = '';
  }

  _handleToggle(event) {
    event.preventDefault();
    this._isMenuOpen() ? this._closeMenu() : this._openMenu();
  }

  _handleKeydown(event) {
    if (event.key === 'Escape' && this._isMenuOpen()) {
      this._closeMenu();
      if (this.toggle) {
        this.toggle.focus();
      }
    }
  }

  _handleClickOutside(event) {
    if (!this._isMenuOpen()) {
      return;
    }

    const clickedInsideNav = this.nav.contains(event.target);
    const clickedToggle = this.toggle && this.toggle.contains(event.target);

    if (!clickedInsideNav && !clickedToggle) {
      this._closeMenu();
    }
  }

  _handleResize() {
    // Auto-close mobile menu when resizing above breakpoint
    if (window.innerWidth >= this.config.mobileBreakpoint && this._isMenuOpen()) {
      this._closeMenu();
    }
  }

  /* ------------------------------------------------------------------
   * Smooth scroll
   * ----------------------------------------------------------------*/

  _handleAnchorClick(event) {
    const href = event.currentTarget.getAttribute('href');
    if (!href || href === '#') {
      return;
    }

    const target = document.querySelector(href);
    if (!target) {
      return;
    }

    event.preventDefault();

    const top = target.getBoundingClientRect().top + window.scrollY - this.config.scrollOffset;

    window.scrollTo({ top, behavior: 'smooth' });

    // Close mobile menu after navigating
    if (this._isMenuOpen()) {
      this._closeMenu();
    }

    // Move focus for accessibility
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------
   * Active link highlighting
   * ----------------------------------------------------------------*/

  _highlightActive() {
    if (!this.sections.length) {
      return;
    }

    let currentId = '';

    this.sections.forEach((section) => {
      const top = section.offsetTop - this.config.scrollOffset - 1;
      if (this._lastScrollY >= top) {
        currentId = section.id;
      }
    });

    this.links.forEach((link) => {
      const isActive = link.getAttribute('href') === `#${currentId}`;
      link.setAttribute('data-state', isActive ? 'active' : '');
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------
   * Public API
   * ----------------------------------------------------------------*/

  /** Tear down all event listeners. */
  destroy() {
    window.removeEventListener('scroll', this._requestTick);
    document.removeEventListener('keydown', this._handleKeydown);
    document.removeEventListener('click', this._handleClickOutside);
    window.removeEventListener('resize', this._handleResize);

    if (this.toggle) {
      this.toggle.removeEventListener('click', this._handleToggle);
    }

    this.links.forEach((link) => {
      link.removeEventListener('click', this._handleAnchorClick);
    });
  }
}
